import { useState, useEffect, useRef, ChangeEvent } from 'react';
import { Input } from './ui/input';

interface HashtagInputProps {
  value: string;
  onEnterPress?: () => void;
  placeholder?: string;
  onChange?: (value: string) => void;
  onTagsChange?: (tags: string[]) => void;
  isEditing?: boolean;
  showTags?: boolean;  // Add this new prop
}

export function HashtagInput({ 
  value, 
  onEnterPress, 
  placeholder, 
  onChange, 
  onTagsChange,
  isEditing = false,
  showTags = true //default to true for backward compatibility
}: HashtagInputProps) {
  const [editText, setEditText] = useState(value);
  const [tags, setTags] = useState<string[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (!isInternalChange.current && value !== editText) {
      setEditText(value);
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    isInternalChange.current = true;
    setEditText(newValue);
    
    const extractedTags = extractTags(newValue);
    setTags(extractedTags);
    
    if (onChange) {
      onChange(newValue);
    }
    if (onTagsChange) {
      onTagsChange(extractedTags);
    }
  };

  const extractTags = (text: string): string[] => {
    const hashtags = text.match(/#[\w-]+/g) || [];
    return hashtags.map((tag) => tag.slice(1));
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget?.closest('.hashtag-input-container')) {
      return;
    }
    setIsFocused(false);
  };

  const handleEnterPressLocal = () => {
    setIsFocused(false);
    if (onEnterPress) {
      onEnterPress();
    }
  };

  return (
    <div className="space-y-2 w-full hashtag-input-container">
      <Input
        ref={inputRef}
        value={editText}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleEnterPressLocal();
          }
        }}
        placeholder={placeholder || "Type your text with #tags..."}
        className="w-full"
      />
      {showTags && !isEditing && !isFocused && tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="bg-primary/10 text-primary px-2 py-1 rounded-full text-sm"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}